<script setup lang="ts">
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { VueFinalModal } from 'vue-final-modal';
import {
  UiModalHeader,
  ColoredButton,
  ThemeProvider,
} from '@/shared/ui';
import PositionFormContent from './PositionFormContent.vue';
import { useDepartmentOptions } from '../lib';
import type { Position } from '@/entities/Position';
import CheckMarkIcon from '@/shared/assets/icons/CheckMarkIcon.svg';

const props = defineProps<{
  title?: string;
  position: Position | null;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'save', data: { position: string; departmentId: number }): void;
}>();

const initialDeptId = computed(() => props.position?.departmentId);

const {
  departmentOptions,
  isLoading: isLoadingDepartments,
  loadDepartments,
  handleSearch,
  initialDepartmentLabel,
} = useDepartmentOptions({ initialDepartmentId: initialDeptId });

const initialFormData = ref<{ position: string; departmentId: string } | null>(null);

const formData = reactive({
  position: '',
  departmentId: '' as string,
});

const hasFormChanges = computed(() => {
  if (!initialFormData.value) return false;
  return (
    formData.position !== initialFormData.value.position ||
    formData.departmentId !== initialFormData.value.departmentId
  );
});

const isFormValid = computed(() => {
  return !!formData.position.trim() && !!formData.departmentId;
});

const handleClose = () => {
  emit('close');
};

const handleSave = () => {
  if (!isFormValid.value || !hasFormChanges.value) return;

  emit('save', {
    position: formData.position,
    departmentId: Number(formData.departmentId),
  });
};

const handleClosed = () => {
  formData.position = '';
  formData.departmentId = '';
  initialFormData.value = null;
};

watch(
  () => props.position,
  (newPosition) => {
    if (newPosition) {
      formData.position = newPosition.name || '';
      formData.departmentId = newPosition.departmentId?.toString() || '';

      initialFormData.value = {
        position: formData.position,
        departmentId: formData.departmentId,
      };
    }
  },
  { immediate: true }
);

onMounted(() => {
  loadDepartments();
});
</script>

<template>
  <VueFinalModal
    class="flex justify-center items-center"
    content-class="flex flex-col bg-white-01 rounded-2xl w-[1000px] shadow-lg"
    overlay-transition="vfm-fade"
    content-transition="vfm-fade"
    @closed="handleClosed"
  >
    <ThemeProvider class="flex flex-col">
      <div class="flex flex-col">
        <UiModalHeader
          class="px-6 py-4 text-sm border-b border-gray-02 flex-shrink-0"
          :title="title || 'Editar Cargo'"
          @close="handleClose"
        />

        <PositionFormContent
          :position="formData.position"
          :department-id="formData.departmentId"
          :department-options="departmentOptions"
          :department-loading="isLoadingDepartments"
          :initial-department-label="initialDepartmentLabel"
          @update:position="formData.position = $event"
          @update:department-id="formData.departmentId = $event"
          @department-search="handleSearch"
        />

        <div class="px-6 py-8 flex justify-end gap-3 border-t border-gray-02 flex-shrink-0">
          <ColoredButton
            text="Cancelar"
            outlined
            color="gray-03"
            @click="handleClose"
          />
          <ColoredButton
            :disabled="!isFormValid || !hasFormChanges"
            text="Salvar"
            :icon="CheckMarkIcon"
            @click="handleSave"
          />
        </div>
      </div>
    </ThemeProvider>
  </VueFinalModal>
</template>

<style scoped>
.vfm__overlay {
  background-color: rgba(0, 0, 0, 0.5);
}
</style>
